import React from 'react';
import type { FC } from 'react';
import { ChevronRight } from 'lucide-react';
import { CHANGE_KIND_CHIP, List, P, Sub, helpSubDomId } from '../ui';
import { CHANGE_KIND_LABEL, HELP_CHANGELOG, RECENT_BADGE_DAYS, releaseTitle } from '../helpChangelog';

interface SectionRef {
  id: string;
  title: string;
}

/**
 * 해당 설명으로 이동. `<a href="#…">`를 쓰지 않는 이유: 해시 이동이 history에 항목을 쌓아
 * 모바일 뒤로가기(useMobileBackNav)가 그것을 화면 단계로 읽는다.
 */
function jumpTo(section: string, anchor?: string) {
  const el =
    (anchor && document.getElementById(helpSubDomId(anchor))) || document.getElementById(`help-${section}`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * 15장 '업데이트 내역'. 장 이름을 쓰려면 HELP_SECTIONS가 필요한데 sections/index.ts가 이 파일을 import하므로,
 * 게터를 받아 렌더 때만 읽는다(순환 import 없음).
 *
 * 릴리스마다 `<Sub>`라 설명서 검색 결과가 릴리스 단위로 나뉜다('2.1.4'로 찾으면 그 릴리스가 나온다).
 * 종류 칩·이동 버튼은 `data-help-meta` — 검색이 읽지 않는다(useHelpSearch).
 */
export function makeChangelogBody(getSections: () => SectionRef[]): FC {
  const ChangelogBody: FC = () => {
    const titles = new Map(getSections().map((s) => [s.id, s.title]));
    return (
      <>
        <P>
          INA-TALK에 새로 생긴 기능과 바뀐 점을 최신순으로 모았습니다. 날짜는 배포한 날입니다. PC 앱은 업데이트를
          설치해야 바뀐 점이 적용되고(버전이 적힌 날은 그 버전부터), 웹 버전과 휴대폰은 새로고침하면 바로 바뀝니다.
          지금 쓰는 PC 앱 버전은 홈 화면 맨 아래에서 볼 수 있습니다.
        </P>
        <P>
          최근 {RECENT_BADGE_DAYS}일 안에 새로 생기거나 바뀐 설명에는 소제목 옆에 <b>새 기능</b>·<b>변경</b> 표시가
          붙습니다. 항목 끝의 장 이름을 누르면 그 설명으로 이동합니다.
        </P>
        {HELP_CHANGELOG.map((release) => (
          <React.Fragment key={`${release.date}:${release.version || ''}`}>
            <Sub>{releaseTitle(release)}</Sub>
            <List>
              {release.changes.map((change, i) => (
                <React.Fragment key={i}>
                  <span
                    data-help-meta=""
                    className={`inline-flex items-center px-1.5 py-0.5 mr-1.5 align-middle text-[11px] font-medium rounded border whitespace-nowrap ${CHANGE_KIND_CHIP[change.kind]}`}
                  >
                    {CHANGE_KIND_LABEL[change.kind]}
                  </span>
                  {change.text}
                  {change.section !== 'changelog' && titles.has(change.section) && (
                    <button
                      type="button"
                      data-help-meta=""
                      onClick={() => jumpTo(change.section, change.anchor)}
                      className="inline-flex items-center ml-1.5 align-middle text-xs font-medium text-brand-700 hover:underline whitespace-nowrap print:no-underline"
                    >
                      {titles.get(change.section)}
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </React.Fragment>
              ))}
            </List>
          </React.Fragment>
        ))}
      </>
    );
  };
  return ChangelogBody;
}
